/**
 * Компонент для отображения статуса лицевого счета
 * 
 * Показывает текущий статус оплаты с цветовой индикацией и дружелюбными сообщениями.
 * Используется в форме оплаты для авторизованных пользователей.
 * Мемоизирован для оптимизации производительности.
 */

import React, { memo } from 'react';
import { Box, Typography, Paper, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { useAccountStatus, PaymentStatus } from '../../../hooks/useAccountStatus';
import { formatCurrency } from '../../../utils/formatHelpers';
import { formatDate } from '../../../utils/dateHelpers';

/**
 * Свойства компонента AccountStatusCard
 */
export interface AccountStatusCardProps {
  /** Текущий баланс счета */
  balance: number;
  /** Дата, до которой оплачены услуги */
  paidUntil: Date | string | null;
  /** Стоимость тарифа за период */
  tariffCost: number;
  /** Флаг блокировки счета */
  isBlocked: boolean;
}

/**
 * Компонент карточки статуса лицевого счета
 * 
 * Отображает статус оплаты с цветовой индикацией:
 * - Зеленый: оплачено более 7 дней
 * - Синий: рекомендуется пополнить (≤7 дней или заблокирован)
 * 
 * Мемоизирован для предотвращения лишних ре-рендеров.
 * 
 * @param props - Свойства компонента
 * @returns JSX элемент карточки статуса
 * 
 * @example
 * ```tsx
 * <AccountStatusCard
 *   balance={500}
 *   paidUntil="2025-11-15"
 *   tariffCost={600}
 *   isBlocked={false}
 * />
 * ```
 */
export const AccountStatusCard: React.FC<AccountStatusCardProps> = memo(({
  balance,
  paidUntil,
  tariffCost,
  isBlocked,
}) => {
  // Вычисляем статус счета
  const status = useAccountStatus({
    paidUntil,
    balance,
    tariffCost,
    isBlocked,
  });

  // Определяем цвет фона в зависимости от статуса
  const getBackgroundColor = () => {
    switch (status.status) {
      case PaymentStatus.PAID:
        return '#e8f5e9'; // Светло-зеленый
      case PaymentStatus.EXPIRING:
      case PaymentStatus.BLOCKED:
        return '#fff3e0'; // Светло-оранжевый (warning)
      default:
        return '#fff3e0';
    }
  };

  // Определяем цвет иконки в зависимости от статуса
  const getIconColor = () => {
    switch (status.status) {
      case PaymentStatus.PAID:
        return '#4caf50'; // Зеленый
      case PaymentStatus.EXPIRING:
      case PaymentStatus.BLOCKED:
        return '#f57c00'; // Оранжевый (warning)
      default:
        return '#f57c00';
    }
  };

  // Выбираем иконку в зависимости от статуса
  const StatusIcon = status.status === PaymentStatus.PAID ? CheckCircleIcon : InfoIcon;

  // Форматируем дату paidUntil для отображения
  const formattedPaidUntil = paidUntil ? formatDate(paidUntil) : 'Не указана';

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        backgroundColor: getBackgroundColor(),
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid',
        borderColor: status.status === PaymentStatus.PAID ? '#c8e6c9' : '#ffcc80',
        
        [theme.breakpoints.down('sm')]: {
          padding: '14px',
        },
      })}
    >
      <Box 
        display="flex" 
        alignItems="flex-start" 
        gap={2}
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: {
            gap: 1.5,
          },
        })}
      >
        {/* Иконка статуса */}
        <StatusIcon
          sx={(theme) => ({
            color: getIconColor(),
            fontSize: '28px',
            flexShrink: 0,
            marginTop: '2px',
            
            [theme.breakpoints.down('sm')]: {
              fontSize: '24px',
            },
          })}
        />

        {/* Контент карточки */}
        <Box flex={1}>
          {/* Сообщение о статусе */}
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: '#212121',
              fontWeight: 500,
              marginBottom: '12px',
              
              [theme.breakpoints.down('sm')]: {
                fontSize: '14px',
                marginBottom: '10px',
              },
            })}
          >
            {status.message}
          </Typography>

          {/* Разделитель */}
          <Divider 
            sx={(theme) => ({
              marginBottom: '12px',
              
              [theme.breakpoints.down('sm')]: {
                marginBottom: '10px',
              },
            })} 
          />

          {/* Информация о счете */}
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={1}
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                gap: 0.75,
              },
            })}
          >
            {/* Баланс */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: '#757575',
                  fontSize: '14px',
                  
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '13px',
                  },
                })}
              >
                Баланс:
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: '#212121',
                  fontWeight: 500,
                  fontSize: '14px',
                  
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '13px',
                  },
                })}
              >
                {formatCurrency(balance)}
              </Typography>
            </Box>

            {/* Дата оплаты */}
            {paidUntil && (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: '#757575',
                    fontSize: '14px',
                    
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '13px',
                    },
                  })}
                >
                  Оплачено до:
                </Typography>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    color: '#212121',
                    fontWeight: 500,
                    fontSize: '14px',
                    
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '13px',
                    },
                  })}
                >
                  {formattedPaidUntil}
                </Typography>
              </Box>
            )}

            {/* Стоимость тарифа */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: '#757575',
                  fontSize: '14px',
                  
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '13px',
                  },
                })}
              >
                Стоимость тарифа:
              </Typography>
              <Typography
                variant="body2"
                sx={(theme) => ({
                  color: '#212121',
                  fontWeight: 500,
                  fontSize: '14px',
                  
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '13px',
                  },
                })}
              >
                {formatCurrency(tariffCost)}/мес
              </Typography>
            </Box>
          </Box>

          {/* Дружелюбное примечание */}
          <Typography
            variant="caption"
            sx={(theme) => ({
              color: '#757575',
              fontSize: '12px',
              marginTop: '12px',
              display: 'block',
              fontStyle: 'italic',
              
              [theme.breakpoints.down('sm')]: {
                fontSize: '11px',
                marginTop: '10px',
              },
            })}
          >
            Вы можете пополнить баланс в любое удобное время
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
});

AccountStatusCard.displayName = 'AccountStatusCard';
